import { useRef, type KeyboardEvent } from 'react'

import { ContentViewerActions } from '@/content-viewer/store/slice'
import type { ContentViewerSession } from '@/content-viewer/store/schemas'
import { useAppDispatch } from '@/store/store-hooks'
import { ChatPanelTabType, DraftWorkspaceActions } from '../store/slice'
import { kChatPanelTabIds } from '../chat-panel-tab-ids'
import { useChatPanelSourceLabel } from '../hooks/useChatPanelSourceLabel'
import PanelTab from '@/components/panel-tab/PanelTab'

const kPaxtonMarkPath = '/images/logos/paxton-logo-svg.svg'

type ChatPanelTabListProps = {
  conversationId: string
  draftArtifactVersionId: string
  /* The source open beside the conversation. */
  sourceSession: ContentViewerSession
  selectedTab: ChatPanelTabType
}

/** The chat side panel's tabs: the conversation, and the source open beside it. */
export default function ChatPanelTabList({ conversationId, draftArtifactVersionId, sourceSession, selectedTab }: ChatPanelTabListProps) {
  const dispatch = useAppDispatch()
  const assistantTabRef = useRef<HTMLButtonElement>(null)
  const sourceTabRef = useRef<HTMLButtonElement>(null)
  const { title, label: sourceLabel } = useChatPanelSourceLabel({ conversationId, sourceSession })

  const selectTab = (tab: ChatPanelTabType) => dispatch(DraftWorkspaceActions.setChatPanelTab({ draftArtifactVersionId, tab }))

  // Only ever two tabs, so either arrow key means the other one, which it opens as well as focuses.
  const handleTabKeyDown = (event: KeyboardEvent<HTMLButtonElement>, tab: ChatPanelTabType) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return
    event.preventDefault()

    if (tab === ChatPanelTabType.Assistant) {
      selectTab(ChatPanelTabType.Source)
      sourceTabRef.current?.focus()
    } else {
      selectTab(ChatPanelTabType.Assistant)
      assistantTabRef.current?.focus()
    }
  }

  return (
    <div role="tablist" aria-label="Chat panel" className="flex min-w-0 flex-1 items-start gap-1">
      <PanelTab
        label="AI Assistant"
        id={kChatPanelTabIds[ChatPanelTabType.Assistant].tab}
        controlsId={kChatPanelTabIds[ChatPanelTabType.Assistant].content}
        selected={selectedTab === ChatPanelTabType.Assistant}
        onSelect={() => selectTab(ChatPanelTabType.Assistant)}
        tabRef={assistantTabRef}
        onKeyDown={(event) => handleTabKeyDown(event, ChatPanelTabType.Assistant)}
        icon={<img src={kPaxtonMarkPath} alt="" className="size-5 shrink-0" />}
        className="shrink-0"
      />
      {/* Sized to its title, shrinking and truncating only once it runs out of room beside the conversation's tab. */}
      <PanelTab
        label={sourceLabel}
        id={kChatPanelTabIds[ChatPanelTabType.Source].tab}
        controlsId={kChatPanelTabIds[ChatPanelTabType.Source].content}
        selected={selectedTab === ChatPanelTabType.Source}
        onSelect={() => selectTab(ChatPanelTabType.Source)}
        tabRef={sourceTabRef}
        onKeyDown={(event) => handleTabKeyDown(event, ChatPanelTabType.Source)}
        onClose={() => dispatch(ContentViewerActions.clearContentViewer())}
        closeLabel={title}
      />
    </div>
  )
}
